import React, { useState, useEffect, useRef } from 'react';

/**
 * ChromeStatusBar — Native Chrome bottom-left status bar idle prompt.
 *
 * Appears 3.5s after page load if the user has not yet started the game.
 * Disappears instantly (display:none, no fade) when the user presses Space or taps.
 */

interface ChromeStatusBarProps {
  gameStarted: boolean;
}

export const ChromeStatusBar: React.FC<ChromeStatusBarProps> = ({ gameStarted }) => {
  const [visible, setVisible] = useState(false);
  const [isTouch, setIsTouch] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const touch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    setIsTouch(touch);
  }, []);

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      setVisible(true);
    }, 3500);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (gameStarted) {
      if (timerRef.current) clearTimeout(timerRef.current);
      setVisible(false);
    }
  }, [gameStarted]);

  if (!visible) return null;

  const prompt = isTouch ? 'Waiting for screen tap...' : 'Waiting for Spacebar...';

  return (
    <div
      aria-live="polite"
      aria-label={prompt}
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        zIndex: 9999,
        background: '#E9E9E9',
        color: '#505050',
        fontFamily: "'Segoe UI', Roboto, Arial, sans-serif",
        fontSize: '12px',
        fontWeight: 400,
        padding: '2px 6px',
        borderTop: '1px solid #DADADA',
        borderRight: '1px solid #DADADA',
        borderBottom: 'none',
        borderLeft: 'none',
        borderRadius: '0 4px 0 0',
        lineHeight: 1.4,
        userSelect: 'none',
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
      }}
    >
      {prompt}
    </div>
  );
};
